import React, { useRef, useState, useEffect, useCallback } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { StageDropdown } from "@/core/MediaItem/StageDropdown";
import { MusicNoteIcon, PlayIcon, PauseIcon } from "@/components/icons";
import { useAdminMediaActions } from "./hooks/useAdminMediaActions";
import { ContextMenu } from "./ContextMenu";

interface AudioMediaCardProps {
  item: MediaItem;
  isSelected: boolean;
  onSelect: (id: number, event: React.MouseEvent) => void;
  onClick: () => void;
  onStageChange: (updatedItem: MediaItem, oldStage: MediaStage) => void;
  /**
   * Context-menu outcomes. Surfaces that own their own list cache (the idea
   * detail Media panel) need these to refetch; the library does not, so both
   * stay optional.
   */
  onMediaItemDeleted?: (id: number) => void;
  onActionError?: (error: string) => void;
}

/**
 * Format a file size in bytes to a human-readable string (KB, MB).
 */
function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format seconds into mm:ss display string.
 */
function formatDuration(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

/**
 * Truncate a filename to a max character length, preserving the extension.
 */
function truncateFilename(name: string, maxLength: number = 28): string {
  if (name.length <= maxLength) return name;
  const extIndex = name.lastIndexOf(".");
  if (extIndex === -1) return name.slice(0, maxLength - 3) + "...";
  const ext = name.slice(extIndex);
  const base = name.slice(0, maxLength - ext.length - 3);
  return `${base}...${ext}`;
}

/**
 * Card component for audio files in the library grid.
 *
 * Displays filename, file type icon, file size, an inline play/pause button,
 * a progress bar, a stage badge, and a selection checkbox for batch operations.
 */
export const AudioMediaCard: React.FC<AudioMediaCardProps> = ({
  item,
  isSelected,
  onSelect,
  onClick,
  onStageChange,
  onMediaItemDeleted,
  onActionError,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const {
    handleStageChange,
    contextMenuItems,
  } = useAdminMediaActions({
    mediaItem: item,
    clientId: item.frontendData.clientId,
    onStageChange,
    onMediaItemDeleted,
    onActionError,
  });

  // Stop playback if the card is removed from the DOM
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
      }
    };
  }, []);

  // Sync audio events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  const togglePlay = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const audio = audioRef.current;
      if (!audio) return;

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        audio.play().catch(() => setIsPlaying(false));
        setIsPlaying(true);
      }
    },
    [isPlaying],
  );

  const handleCardClick = useCallback(() => {
    onClick();
  }, [onClick]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(item.id, e);
    },
    [onSelect, item.id],
  );

  const streamUrl = item.getAudioStreamUrl();
  const filename = item.originalFilename || "Untitled";
  const fileSize = formatFileSize(item.fileSize);

  return (
    <div
      className={`group/card relative bg-white rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${
        isSelected ? "ring-2 ring-blue-500" : ""
      }`}
      onClick={handleCardClick}
    >
      {/* Hidden audio element for inline playback */}
      <audio ref={audioRef} src={streamUrl} preload="none" />

      {/* Top row: checkbox + stage + context menu */}
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <div className="flex items-center gap-2">
          {/* Selection checkbox */}
          <button
            onClick={handleCheckboxClick}
            data-interactive="true"
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all shrink-0 ${
              isSelected
                ? "bg-blue-500 border-blue-500"
                : "bg-white border-gray-300 opacity-0 group-hover/card:opacity-100"
            }`}
          >
            {isSelected && (
              <CheckSvg />
            )}
          </button>

          {/* Stage dropdown */}
          <div data-interactive="true">
            <StageDropdown
              mediaItem={item}
              onStageChange={handleStageChange}
              isChanging={false}
              disabled={false}
            />
          </div>
        </div>

        {/* Context menu */}
        <div data-interactive="true" className="opacity-0 group-hover/card:opacity-100 transition-opacity">
          <ContextMenu items={contextMenuItems} />
        </div>
      </div>

      {/* Content area: icon + info */}
      <div className="flex items-center gap-3 px-3 py-3">
        {/* Play/pause button with music note background */}
        <button
          onClick={togglePlay}
          data-interactive="true"
          className="relative w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-200 transition-colors"
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <PauseIcon className="w-4 h-4 text-gray-700" />
          ) : (
            <PlayIcon className="w-4 h-4 text-gray-700 ml-0.5" />
          )}
        </button>

        {/* File info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={filename}>
            {truncateFilename(filename)}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <MusicNoteIcon className="w-3 h-3 shrink-0" />
            {fileSize && <span>{fileSize}</span>}
            {duration > 0 && (
              <>
                {fileSize && <span>-</span>}
                <span>{formatDuration(duration)}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100">
        <div
          className="h-full bg-blue-500 transition-all duration-100"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Playback time indicator (visible during playback) */}
      {isPlaying && duration > 0 && (
        <div className="px-3 py-1 text-xs text-gray-400 text-right">
          {formatDuration(currentTime)} / {formatDuration(duration)}
        </div>
      )}
    </div>
  );
};

/**
 * Tiny check SVG for the selection checkbox.
 * Uses an inline SVG because it is a 3-line path specific to this checkbox,
 * not a general-purpose icon worth adding to the icon library.
 */
function CheckSvg() {
  return (
    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
    </svg>
  );
}
